import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { checkIfHotjarEnable, capitalizeFirstLetter, timeBetweenDates } from '../utils/helpers';
import { coveragePlans } from '../data/coveragePlan';
import history from '../utils/history';
import * as sinisterCardsActions from '../redux/actions/sinister-cards';
import Input from '../components/common/input/Input';
import RimacTag from '../components/common/RimacTag/RimacTag';
import SplitLayout from '../layout/SplitLayout';
import IconWarning from '../assets/icons/ic_warning_yellow.svg';

const AmountInfoPage = () => {
  const [viewStartTime] = useState(new Date());
  const dispatch = useDispatch();
  const userFlowType = useSelector((state) => state.coverages.userFlowType);
  const sinisterList = useSelector((state) => state.sinisterCards.sinisterList);
  const currentCoveragePlan = useSelector(
    (state) => state.coverages.currentPlan
  );
  const currentUser = useSelector((state) => state.sinisterCards.userInfo);
  const currentUserName = `${capitalizeFirstLetter(
    currentUser?.name ?? ''
  )} ${capitalizeFirstLetter(currentUser?.fatherLastName ?? '')}`;
  const sinistersAmounts = [];

  sinisterList.forEach((sinister) => {
    let checked = '';
    if (sinister.soles && !sinister.dollars) {
      checked = 'soles';
    } else if (!sinister.soles && sinister.dollars) {
      checked = 'dollars';
    } else if (sinister.soles && sinister.dollars) {
      checked = 'ambos';
    }

    sinistersAmounts.push({
      icon: sinister.icon,
      code: sinister.code,
      relativeNum: sinister.relativeNum,
      title: sinister.title,
      soles: sinister.soles,
      dollars: sinister.dollars,
      warningTxt: sinister.warningTxt,
      amountInfo: sinister.amountInfo,
      solesError: '',
      dollarsError: '',
      checked,
    });
  });
  const [amounts, setAmounts] = useState(sinistersAmounts);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      let elem = document.getElementById(location.hash.slice(1));
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    if (!viewStartTime) return;
    const endTime = new Date();
    const userTotalTime = timeBetweenDates(viewStartTime, endTime);

    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        task: {
          name: 'Tiempo registrado - Montos Robados',
          time: userTotalTime,
        },
        event: 'trackTimeToComplete',
      });
    }
  }, [viewStartTime]);

  const onRadioClick = (index, checked) => {
    const newAmounts = [...amounts];
    newAmounts[index].checked = checked;

    if (checked === 'soles') {
      newAmounts[index].dollars = '';
    } else if (checked === 'dollars') {
      newAmounts[index].soles = '';
    }

    setAmounts(newAmounts);
  };

  const inputSolesHandler = (index, val) => {
    const newAmounts = [...amounts];
    const solesAmountMax = 1000000;
    newAmounts[index].soles = val;

    if (Number(val) === 0 && val !== '') {
      newAmounts[index].solesError = 'Por favor ingresa un monto válido';
      setAmounts(newAmounts);
      return;
    }

    if (val >= solesAmountMax) {
      newAmounts[index].solesError = 'Por favor ingresa un monto válido';
      setAmounts(newAmounts);
      return;
    }

    newAmounts[index].solesError = '';
    setAmounts(newAmounts);
  };

  const inputDollarsHandler = (index, val) => {
    const newAmounts = [...amounts];
    const dollarsAmountMax = 90000;
    newAmounts[index].dollars = val;

    if (Number(val) === 0 && val !== '') {
      newAmounts[index].dollarsError = 'Por favor ingresa un monto válido';
      setAmounts(newAmounts);
      return;
    }

    if (val > dollarsAmountMax) {
      newAmounts[index].dollarsError = 'Por favor ingresa un monto válido';
      setAmounts(newAmounts);
      return;
    }

    newAmounts[index].dollarsError = '';
    setAmounts(newAmounts);
  };

  const isNextActive = () => {
    const isAnyError = amounts.some(
      (amount) => amount.solesError || amount.dollarsError
    );
    const noChecks = amounts.some((amount) => !amount.checked);
    const amountError = amounts.map((sinister) => {
      return !!(
        (sinister.checked === 'soles' && !sinister.soles) ||
        (sinister.checked === 'dollars' && !sinister.dollars) ||
        (sinister.checked === 'ambos' && (!sinister.soles || !sinister.dollars))
      );
    });
    if (isAnyError) {
      if (checkIfHotjarEnable()) {
        // LAST
        window.digitalData.push({
          "log": {
            "message": "Por favor ingresa un monto válido"
          },
          "event": "trackLog"
        });
      }
    }
    return isAnyError || noChecks || amountError.includes(true) ? true : false;
  };

  const onBackHandler = () => {
    dispatch(sinisterCardsActions.reduceStep({plan: currentCoveragePlan}));
    history.replace(`/${coveragePlans[currentCoveragePlan].uri}/documentos-requeridos`);
  };

  const setDigitalData = (value) => {
    // LAST
    window.digitalData.push({
      "metadata": {
        "key": "Monto - Protección Tarjeta BBVA",
        "value": value
      },
      "event": "trackMetadata"
    });
  }
  const onContinueHandler = () => {
    let isNewAccountRequired = false;
    const lastTotalAmountSoles = sinisterList.reduce(
      (prev, curr) => (curr.soles ? prev + Number(curr.soles) : prev),
      0
    );
    const lastTotalAmountDollars = sinisterList.reduce(
      (prev, curr) => (curr.dollars ? prev + Number(curr.dollars) : prev),
      0
    );
    const updatedSinisterList = [...sinisterList];
    updatedSinisterList.forEach((sinister) => {
      const findSinisterAmount = amounts.find(
        (amount) => amount.code === sinister.code
      );
      sinister.soles = findSinisterAmount.soles;
      sinister.dollars = findSinisterAmount.dollars;
    });

    if (checkIfHotjarEnable()) {
      let mensaje = `${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())}`;
      if (updatedSinisterList.length === 1) {
        mensaje += ` - ${updatedSinisterList[0].title}`
        if (updatedSinisterList[0].soles && updatedSinisterList[0].dollars) {
          // 1 cob y Ambas monedas (<plan> - Ambos - <monto-soles> - <monto-dólares>)
          mensaje += ` - Ambos - S/${updatedSinisterList[0].soles} - $/${updatedSinisterList[0].dollars}`;
        } else if (updatedSinisterList[0].soles) {
          // 1 cobertura y un tipo de moneda sol (<plan> - <tipo-moneda> - <monto>)
          mensaje += ` - Soles - ${updatedSinisterList[0].soles}`
        } else {
          // 1 cobertura y un tipo de moneda dolar (<plan> - <tipo-moneda> - <monto>)
          mensaje +=` - Dólares - ${updatedSinisterList[0].dollars}`;
        }
      } else {
        for (const amount of updatedSinisterList) {
          mensaje += `|${amount.title} - `
          if (amount.soles && amount.dollars) {
            // cobertura y Ambas monedas
            mensaje += `Ambos - S/${amount.soles} - $/${amount.dollars}`;
          } else if (amount.soles) {
            // cobertura y tipo de moneda soles
            mensaje += `Soles - ${amount.soles}`;
          } else {
            // cobertura y tipo de moneda dolars
            mensaje += `Dólares - ${amount.dollars}`;
          }
        }
        // varias coberturas con su tipo de monedas (`<plan>|<tipo-moneda1> - <monto1>|<tipo-moneda2> - <monto2>|tipo-moneda3> - <monto3>`)
        // varias coberturas con ambas monedas (`<plan> | Ambos - <monto1-soles> - <monto1-dólares>|`)
      }
      setDigitalData(mensaje);
    }
    const newTotalAmountSoles = updatedSinisterList.reduce(
      (prev, curr) => (curr.soles ? prev + Number(curr.soles) : prev),
      0
    );
    const newTotalAmountDollars = updatedSinisterList.reduce(
      (prev, curr) => (curr.dollars ? prev + Number(curr.dollars) : prev),
      0
    );

    if (
      lastTotalAmountSoles !== newTotalAmountSoles ||
      lastTotalAmountDollars !== newTotalAmountDollars
    ) {
      isNewAccountRequired = true;
    }

    dispatch(sinisterCardsActions.updateSinisterList(updatedSinisterList));

    if (userFlowType === 'input') {
      dispatch(sinisterCardsActions.incrementStep());
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/info-reembolso`);
    } else if (userFlowType === 'edit' && isNewAccountRequired) {
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/info-reembolso`);
    } else {
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/resumen`);
    }
  };

  const sortedAmounts = amounts.sort(
    (a, b) => Number(a.relativeNum) - Number(b.relativeNum)
  );

  const leftContent = (
    <>
      <RimacTag
        customClasses="u-mb-8"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <div className="titleCont">
        <h1 className="u-mb-40">Seguro de Protección de Tarjetas</h1>
        <p>
          <span className="u-font-bold">{currentUserName}</span>, estás
          solicitando:
        </p>
        <ul className="rimacCardCont">
          {sinisterList.map((cov) => (
            <div key={cov.code} className="rimacLiteCard u-mb-8">
              <img
                className="rimacLiteCard__img"
                src={cov.icon}
                alt={cov.title}
              />
              <p className="rimacLiteCard__title">{cov.title}</p>
            </div>
          ))}
        </ul>
      </div>
    </>
  );

  const rightContent = (
    <>
      <RimacTag
        customClasses="u-mb-16 mobile"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <h1 className="titleMobile">¿En qué moneda fue el siniestro?</h1>
      {sortedAmounts.map((sinister, i) => (
        <section
          id={sinister.code}
          key={`cont-${sinister.code}`}
          className={'amountSection u-mb-40'}
        >
          <div className="titleCont new">
            <div className="titleCont__title u-mb-16-0">
              <img src={sinister.icon} alt={sinister.title} />
              <h3>{sinister.title}</h3>
            </div>
          </div>

          <div className="inputCont">
            {sinister.warningTxt && (
              <p className="u-mb-16 amountWarningTxt">
                <b>Recuerda: </b> {sinister.warningTxt}
              </p>
            )}
            <h4 className="u-mb-16 titleDesktop">
              ¿En qué moneda fue el siniestro?
            </h4>
            <p className="u-mb-24">Selecciona una de las opciones</p>

            <fieldset className="checkCont u-mb-16">
              <label
                className={`radioBtnGen ${
                  sinister.checked === 'soles' ? 'selected' : ''
                }`}
                htmlFor={`check-${sinister.code}-1`}
              >
                <input
                  id={`check-${sinister.code}-1`}
                  type="radio"
                  name={`radio-${sinister.code}`}
                  checked={sinister.checked === 'soles'}
                  onChange={() => onRadioClick(i, 'soles')}
                />
                <span className="radioBtnGen__checkmark u-mr-12"></span>
                Soles (S/)
              </label>

              <label
                className={`radioBtnGen ${
                  sinister.checked === 'dollars' ? 'selected' : ''
                }`}
                htmlFor={`check-${sinister.code}-2`}
              >
                <input
                  id={`check-${sinister.code}-2`}
                  type="radio"
                  name={`radio-${sinister.code}`}
                  checked={sinister.checked === 'dollars'}
                  onChange={() => onRadioClick(i, 'dollars')}
                />
                <span className="radioBtnGen__checkmark u-mr-12"></span>
                Dólares ($)
              </label>

              <label
                className={`radioBtnGen ${
                  sinister.checked === 'ambos' ? 'selected' : ''
                }`}
                htmlFor={`check-${sinister.code}-3`}
              >
                <input
                  id={`check-${sinister.code}-3`}
                  type="radio"
                  name={`radio-${sinister.code}`}
                  checked={sinister.checked === 'ambos'}
                  onChange={() => onRadioClick(i, 'ambos')}
                />
                <span className="radioBtnGen__checkmark u-mr-12"></span>
                Ambos
              </label>
            </fieldset>

            {sinister.checked && (
              <div className="amountWrapper">
                <h4 className="u-mb-16">
                  {sinister.checked === 'ambos'
                    ? '¿Cuáles fueron los montos?'
                    : '¿Cuál fue el monto?'}
                </h4>
                {sinister.checked === 'soles' ||
                sinister.checked === 'ambos' ? (
                  <div className="amountWrapper__inputCont">
                    <Input
                      type="time"
                      isCurrency
                      label="Monto en Soles (S/)"
                      maxLength={9}
                      currencyValue={sinister.soles}
                      onChange={(val) => inputSolesHandler(i, val)}
                      hasError={sinister.solesError}
                    />
                  </div>
                ) : null}
                {sinister.checked === 'dollars' ||
                sinister.checked === 'ambos' ? (
                  <div className="amountWrapper__inputCont">
                    <Input
                      className={sinister.checked === 'ambos' && 'u-mt-16'}
                      isCurrency
                      currencyPrefix={'$ '}
                      type="time"
                      label="Monto en Dólares ($)"
                      maxLength={8}
                      currencyValue={sinister.dollars}
                      onChange={(val) => inputDollarsHandler(i, val)}
                      hasError={sinister.dollarsError}
                    />
                  </div>
                ) : null}
              </div>
            )}
            {sinister.checked && sinister.amountInfo ? (
              <div className="amountWarning">
                <img
                  className="amountWarning__img"
                  src={IconWarning}
                  alt="cobertura máxima"
                />
                <p className="amountWarning__text">{sinister.amountInfo}</p>
              </div>
            ) : null}
          </div>
        </section>
      ))}
    </>
  );

  return (
    <SplitLayout
      dataTestId='amount-info-page'
      containerClasses="amountInfoPage"
      leftClasses="amountInfoPage__left"
      rightClasses="amountInfoPage__right"
      leftContent={leftContent}
      rightContent={rightContent}
      isNavActive={userFlowType === 'input'}
      isNavNextDisabled={isNextActive()}
      onNavBackClick={onBackHandler}
      onNavContinueClick={onContinueHandler}
      navCurrentStep={4}
      navTotalSteps={5}
      navStepperText={'PASO 4 DE 5'}
      navBtnText="Continuar"
    ></SplitLayout>
  );
};

export default AmountInfoPage;
